import { useConfirm } from "../confirm/ConfirmProvider";
import { toast } from "../toast/toast";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  Lab,
  Reagent,
  ReagentGroup,
  ReagentSize,
  StorageLocation,
  Unit,
  User,
} from "../../../backend/src/types";
import { useUser } from "../contexts/UserContext";
import {
  getAlertsCount,
  handleAlert,
  HandleAlertPayload,
} from "../fetch/AlertRequest";
import {
  getReagentGroupList,
  getReagentSizeList,
} from "../fetch/ReagentGroupRequest";
import {
  addReagent,
  disposeReagent,
  editReagent,
} from "../fetch/ReagentRequest";
import { getStorageLocations } from "../fetch/StorageRequest";
import { toJSTDateString } from "../function/toJSTDateString";
import styles from "../styles/modules/AdminPage.module.css";
import { QRCode } from "./QRCode";
import RequiredMark from "./RequiredMark";

interface ReagentPopupProps {
  targetReagent: Reagent | null;
  selectedLab: Lab | null;
  units: Unit[];
  targetReagentGroup: ReagentGroup;
  onClose: () => void;
  onLoading: (start: boolean) => void;
  updateReagentList: (updateReagent: Reagent) => void;
}

const ReagentPopup: React.FC<ReagentPopupProps> = ({
  targetReagent,
  selectedLab,
  units,
  targetReagentGroup,
  onClose,
  onLoading,
  updateReagentList,
}) => {
  const { user, userLabs, setAlertCount } = useUser();
  const confirm = useConfirm();
  const isEdit = targetReagent !== null;
  const [reagentSizes, setReagentSizes] = useState<ReagentSize[]>([]);
  const [reagentGroups, setReagentGroups] = useState<ReagentGroup[]>([]);

  const [selectedReagentSizeId, setSelectedReagentSizeId] = useState<number>(0);

  const [oldExpires, setOldExpires] = useState<string | null>(null);
  const [reagent, setReagent] = useState<Reagent>({
    id: 0,
    tenant_id: user?.tenant_id ?? 0,
    name: "",
    lab_id: selectedLab?.id ?? 0,
    group_id: 0,
    capacity_base: 0,
    quantity_base: 0,
    min_quantity_base: 0,
    unit_kind: "",
    display_unit: "",
    purchased_at: "",
    expires_at: null,
    location_code: "",
    storage_location_id: null,
    status: "active",
    manufacturer: null,
    order_number: null,
    created_at: "",
    updated_at: "",
  });
  const [storageLocations, setStorageLocations] = useState<StorageLocation[]>(
    [],
  );

  useEffect(() => {
    if (!user || !selectedLab) {
      return;
    }

    loadBaseData(user);

    if (targetReagent) {
      loadReagentData(targetReagent);
    }
  }, [user, targetReagent]);

  // 研究室の保管場所（試薬庫）一覧をプルダウン用に取得
  useEffect(() => {
    if (selectedLab) {
      getStorageLocations(selectedLab.id).then(setStorageLocations);
    }
  }, [selectedLab]);

  useEffect(() => {
    if (!reagent.group_id) {
      return;
    }

    callLoadReagentSizes(reagent.group_id);
  }, [reagent.group_id]);

  useEffect(() => {
    if (!selectedReagentSizeId) {
      return;
    }
    loadReagentSizesById(selectedReagentSizeId);
  }, [selectedReagentSizeId, reagentSizes]);

  function loadReagentSizesById(selectedReagentSizeId: number) {
    const selectedSize = reagentSizes.find(
      (size) => size.id === selectedReagentSizeId,
    );

    if (!selectedSize) {
      return;
    }

    setReagent((prev) => ({
      ...prev,
      capacity_base: selectedSize.capacity_base,
      display_unit: selectedSize.unit,
      min_quantity_base:
        !isEdit && prev.display_unit !== selectedSize.unit
          ? 0
          : prev.min_quantity_base,
    }));
  }

  async function loadBaseData(user: User) {
    const reagentGroups = await getReagentGroupList({
      tenant_id: user.tenant_id,
      lab_id: selectedLab?.id ?? 0,
    });

    onLoading(true);

    setReagentGroups(reagentGroups);
    setReagent((prev) => ({
      ...prev,
      group_id: targetReagentGroup.id,
      // 登録時は試薬名の初期値に試薬マスター名を入れる（編集時は既存値を維持）
      name: isEdit ? prev.name : targetReagentGroup.name,
    }));

    onLoading(false);
  }

  async function loadReagentData(targetReagent: Reagent) {
    if (targetReagent.expires_at) {
      setOldExpires(targetReagent.expires_at);
    }

    setReagent({
      id: targetReagent.id,
      tenant_id: targetReagent.tenant_id,
      name: targetReagent.name,
      lab_id: targetReagent.lab_id,
      group_id: targetReagent.group_id,
      capacity_base: targetReagent.capacity_base,
      quantity_base: targetReagent.quantity_base,
      min_quantity_base: targetReagent.min_quantity_base,
      unit_kind: targetReagent.unit_kind,
      display_unit: targetReagent.display_unit,
      purchased_at: targetReagent.purchased_at
        ? toJSTDateString(targetReagent.purchased_at)
        : "",
      expires_at: targetReagent.expires_at
        ? toJSTDateString(targetReagent.expires_at)
        : null,
      location_code: targetReagent.location_code,
      storage_location_id: targetReagent.storage_location_id ?? null,
      status: targetReagent.status,
      manufacturer: targetReagent.manufacturer ?? null,
      order_number: targetReagent.order_number ?? null,
      created_at: targetReagent.created_at,
      updated_at: targetReagent.updated_at,
    });
  }

  async function callLoadReagentSizes(selectedReagentGroupId: number) {
    onLoading(true);
    const sizes = await getReagentSizeList({
      group_id: selectedReagentGroupId,
    });

    setReagentSizes(sizes);

    if (targetReagent && selectedReagentGroupId === targetReagent.group_id) {
      const matchedSize = sizes.find(
        (size) =>
          size.capacity_base === targetReagent.capacity_base &&
          size.unit === targetReagent.display_unit,
      );

      if (matchedSize) {
        setSelectedReagentSizeId(matchedSize.id);
      }
    }

    onLoading(false);
  }

  async function handleAddReagent() {
    const updatedReagent = validateReagent(reagent);
    if (updatedReagent === null || !user) {
      return;
    }

    onLoading(true);

    const reagentRes = await addReagent({ user, updatedReagent });

    if (reagentRes != null) {
      await handleAddAlert(reagentRes);
      updateReagentList(reagentRes);
      toast.success(`${updatedReagent.name}を登録しました。`);
      onClose();
    }

    onLoading(false);
  }

  async function handleEditReagent() {
    if (!targetReagent) {
      return;
    }

    const updatedReagent = validateReagent(reagent);
    if (updatedReagent === null || !user) {
      return;
    }

    onLoading(true);

    const reagentRes = await editReagent({ user, updatedReagent });

    if (reagentRes != null) {
      await handleEditAlert(reagentRes);
      updateReagentList(reagentRes);
      toast.success(`${updatedReagent.name}を更新しました。`);
      onClose();
    }
    onLoading(false);
  }

  function validateReagent(reagent: Reagent): Reagent | null {
    // 保管場所備考は自由記入・任意
    const location_code = reagent.location_code?.trim() || null;

    const name = reagent?.name?.trim() ?? "";
    if (name === "") {
      toast.error("名前が未入力または空白のみで構成されています。");
      return null;
    }
    const manufacturer = reagent?.manufacturer?.trim() ?? "";
    const order_number = reagent?.order_number?.trim() ?? "";

    const unit = units.find((u) => u.code === reagent.display_unit);
    const unit_kind = unit?.kind;
    if (unit_kind == null) {
      toast.error("不正な単位が指定されています。");
      return null;
    }

    //2回使うので比較用関数を切り出し
    const checkUnitKind = (targetUnitCode: string | undefined | null) => {
      const compareUnit = units?.find((u) => u.code === targetUnitCode);
      if (unit && compareUnit && unit_kind !== compareUnit.kind) {
        toast.error(
          "単位の系統が試薬マスターで設定されたものから変更されています。",
        );
        return true;
      }
      return false;
    };

    const group = reagentGroups?.find((g) => g.id === reagent.group_id);
    if (checkUnitKind(group?.total_unit)) {
      return null;
    }

    const updatedReagent = {
      ...reagent,
      name,
      location_code,
      manufacturer,
      order_number,
      unit_kind: unit_kind,
    };

    if (
      !updatedReagent.tenant_id ||
      !updatedReagent.name ||
      !updatedReagent.lab_id ||
      !updatedReagent.group_id ||
      !updatedReagent.display_unit ||
      !updatedReagent.unit_kind ||
      !updatedReagent.purchased_at ||
      updatedReagent.capacity_base == null ||
      (isEdit && updatedReagent.quantity_base == null)
    ) {
      toast.error("必須項目のいずれかが未入力です。");
      return null;
    }

    if (updatedReagent.capacity_base <= 0) {
      toast.error("容量は0を超える値を入力してください。");
      return null;
    }

    if (updatedReagent.expires_at) {
      const purchasedDate = new Date(updatedReagent.purchased_at);
      const expiresDate = new Date(updatedReagent.expires_at);

      if (expiresDate < purchasedDate) {
        toast.error("有効期限が購入日よりも前になっています。");
        return null;
      }
    }

    if (isEdit) {
      if (updatedReagent.capacity_base < updatedReagent.quantity_base) {
        toast.error("残量が容量を超えています");
        return null;
      }
    }

    if (updatedReagent.min_quantity_base < 0) {
      toast.error("最低残量は0以上の値を入力してください。");
      return null;
    }

    if (updatedReagent.capacity_base < updatedReagent.min_quantity_base) {
      toast.error("最低残量が容量を超えています。");
      return null;
    }

    if (
      updatedReagent.unit_kind === "count" &&
      !Number.isInteger(updatedReagent.min_quantity_base)
    ) {
      toast.error("単位種別が個数の場合、最低残量は整数のみを入力してください。");
      return null;
    }

    return updatedReagent;
  }

  async function refreshAlertCount() {
    if (user?.tenant_id) {
      const tenant_id = user.tenant_id;
      const lab_ids = userLabs.map((lab) => lab.id);
      const alertCount = await getAlertsCount({ tenant_id, lab_ids });
      setAlertCount(alertCount);
    }
  }

  async function syncReagentAlerts(reagentRes: Reagent) {
    const alertBase: Omit<HandleAlertPayload, "category"> = {
      tenant_id: reagentRes.tenant_id,
      lab_id: reagentRes.lab_id,
      reagent_id: reagentRes.id,
    };

    await handleAlert({
      ...alertBase,
      category: "expire_soon",
    });

    await handleAlert({
      ...alertBase,
      category: "low_quantity",
    });

    await refreshAlertCount();
  }

  async function handleAddAlert(reagentRes: Reagent) {
    await syncReagentAlerts(reagentRes);
  }

  async function handleEditAlert(reagentRes: Reagent) {
    await syncReagentAlerts(reagentRes);
  }

  async function handleDisposeReagent() {
    if (!targetReagent) {
      return;
    }

    if (targetReagent.status === "disposed") {
      toast.error("その試薬はすでに廃棄されています。");
      return;
    }

    onLoading(true);

    if (
      await confirm("本当に廃棄しますか？", {
        danger: true,
        confirmText: "廃棄",
      })
    ) {
      await disposeReagent({ reagent_id: targetReagent.id });

      const updatedReagent = {
        ...targetReagent,
        quantity_base: 0,
        status: "disposed",
      };

      await handleDisposeAlert(updatedReagent);
      updateReagentList(updatedReagent);
      onLoading(false);
      onClose();
    }

    onLoading(false);
  }

  async function handleDisposeAlert(reagentRes: Reagent) {
    let alert: HandleAlertPayload = {
      tenant_id: reagentRes.tenant_id,
      lab_id: reagentRes.lab_id,
      category: "",
    };

    alert.reagent_id = reagentRes.id;
    alert.category = "discarded";
    await handleAlert(alert);

    //初期化対象である期日間近アラートについては、バックエンドでdisposeする際に初期化を行っている。

    await refreshAlertCount();
  }

  const ReagentPopUpContent = (
    <div className={styles["popup-background"]} onClick={() => onClose()}>
      <div
        className={styles["popup-section"]}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>{isEdit ? "試薬情報の編集" : "試薬の登録"}</h3>
        <form
          onKeyDown={(e) => {
            // 入力欄での Enter による暗黙送信を防ぐ（登録/更新はボタン押下のみ）
            if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
              e.preventDefault();
            }
          }}
          onSubmit={(e) => {
            e.preventDefault();
            isEdit ? handleEditReagent() : handleAddReagent();
          }}
        >
          <label>
            <span>
              試薬マスター
              <RequiredMark />
            </span>
            <select
              className="select"
              value={reagent.group_id}
              onChange={(e) =>
                setReagent((prev) => ({
                  ...prev,
                  group_id: Number(e.target.value),
                }))
              }
              required
              disabled
            >
              <option value="">試薬マスターを選択してください</option>
              {reagentGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>
              試薬名
              <RequiredMark />
            </span>
            <input
              type="text"
              placeholder="例：エタノール（500mL）"
              value={reagent.name}
              onChange={(e) =>
                setReagent((prev) => ({ ...prev, name: e.target.value }))
              }
              required
            />
          </label>
          <label>
            <span>
              容量
              <RequiredMark />
            </span>
            <select
              className="select"
              value={selectedReagentSizeId ?? ""}
              onChange={(e) => {
                setSelectedReagentSizeId(Number(e.target.value));
              }}
              required
              disabled={isEdit}
            >
              <option value="">サイズを選択してください</option>
              {reagentSizes.map((size) => (
                <option key={size.id} value={size.id}>
                  {size.capacity_base}
                  {size.unit}
                </option>
              ))}
            </select>
            <span className={styles["field-hint"]}>
              サイズは試薬マスターの設定で登録できます。
            </span>
          </label>
          {isEdit && (
            <label>
              残量
              <div className={styles["unit-input"]}>
                <input
                  type="number"
                  inputMode="numeric"
                  value={reagent.quantity_base}
                  onChange={(e) =>
                    setReagent((prev) => ({
                      ...prev,
                      quantity_base: Number(e.target.value),
                    }))
                  }
                  min={0}
                  step="any"
                  disabled
                />
                <span>{reagent.display_unit ?? ""}</span>
              </div>
            </label>
          )}
          <label>
            <span>最低残量</span>
            <div className={styles["unit-input"]}>
              <input
                type="number"
                inputMode="decimal"
                value={reagent.min_quantity_base}
                onChange={(e) =>
                  setReagent((prev) => ({
                    ...prev,
                    min_quantity_base: Number(e.target.value),
                  }))
                }
                min={0}
                step={reagent.unit_kind === "count" ? 1 : "any"}
              />
              <span>{reagent.display_unit ?? ""}</span>
            </div>
          </label>
          <label>
            保管場所            <select
              value={reagent.storage_location_id ?? ""}
              onChange={(e) =>
                setReagent((prev) => ({
                  ...prev,
                  storage_location_id: e.target.value
                    ? Number(e.target.value)
                    : null,
                }))
              }
            >
              <option value="">未設定</option>
              {storageLocations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
            <span className={styles["field-hint"]}>
              保管場所の設定は研究室選択ページで行えます。
            </span>
          </label>
          <label>
            備考            <input
              type="text"
              maxLength={100}
              value={reagent.location_code ?? ""}
              onChange={(e) =>
                setReagent((prev) => ({
                  ...prev,
                  location_code: e.target.value,
                }))
              }
              placeholder="自由入力"
            />
          </label>
          <label>
            <span>
              購入日
              <RequiredMark />
            </span>
            <input
              type="date"
              value={reagent.purchased_at ?? ""}
              onChange={(e) =>
                setReagent((prev) => ({
                  ...prev,
                  purchased_at: e.target.value,
                }))
              }
              required
            />
          </label>
          <label>
            メーカー名            <input
              type="text"
              placeholder="例：〇〇化学"
              value={reagent.manufacturer ?? ""}
              onChange={(e) =>
                setReagent((prev) => ({
                  ...prev,
                  manufacturer: e.target.value,
                }))
              }
            />
          </label>
          <label>
            注文番号            <input
              type="text"
              placeholder="例：ORD-12345"
              value={reagent.order_number ?? ""}
              onChange={(e) =>
                setReagent((prev) => ({
                  ...prev,
                  order_number: e.target.value,
                }))
              }
            />
          </label>
          <label>
            満了期限            <input
              type="date"
              value={reagent.expires_at ?? ""}
              onChange={(e) =>
                setReagent((prev) => ({
                  ...prev,
                  expires_at: e.target.value,
                }))
              }
            />
          </label>
          {isEdit && (
            <QRCode
              text={`${window.location.origin}/reagents/${targetReagent.id}`}
            />
          )}
          {isEdit && (
            <div>
              <button
                type="button"
                className={styles["dispose-button"]}
                onClick={() => handleDisposeReagent()}
              >
                廃棄
              </button>
            </div>
          )}
          <div className={styles["button-row"]}>
            <button type="submit" className={styles["ok-button"]}>
              {isEdit ? "更新" : "登録"}
            </button>
            <button type="button" onClick={() => onClose()}>
              キャンセル
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    ReagentPopUpContent,
    document.getElementById("popup-root")!,
  );
};

export default ReagentPopup;
